import { Button } from '@ctw/shared/components/button';
import { CopyToClipboard, copyToClipboard } from '@ctw/shared/components/copy-to-clipboard';
import { Heading } from '@ctw/shared/components/heading';
import { JsonViewer } from '@ctw/shared/components/json-viewer';
import { notify } from '@ctw/shared/components/toast';
import { useModal } from '@ctw/shared/context/modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { downloadBlob } from '@ctw/shared/utils/documents';
import { tw } from '@ctw/shared/utils/tailwind';
import type { Resource } from 'fhir/r4';

interface ViewFhirModalBodyProps {
  title: string;
  resource: Resource;
}

const ViewFhirModalBody = ({ title, resource }: ViewFhirModalBodyProps) => {
  const json = JSON.stringify(resource, null, 2);

  return (
    <div className={tw`flex h-full flex-col gap-4`}>
      <div className={tw`space-y-1 pr-10`}>
        <Heading level="h2" className={tw`m-0 text-lg`}>
          {title}
        </Heading>
        <div className={tw`flex items-center gap-2 text-sm`}>
          <span className={tw`font-semibold text-content-main`}>{resource.resourceType}</span>
          {resource.id && (
            <>
              <span className={tw`text-content-subtle`}>·</span>
              <CopyToClipboard content={resource.id} className={tw`font-mono text-content-subtle`}>
                {resource.id}
              </CopyToClipboard>
            </>
          )}
        </div>
      </div>
      <JsonViewer json={resource} className={tw`min-h-0 flex-1`} />
      <div className={tw`flex justify-end gap-2`}>
        <Button
          type="button"
          variant="secondary"
          telemetryInteractionName="resource_action_click"
          telemetryContext={{ resourceActionName: 'Download FHIR' }}
          onClick={() =>
            downloadBlob(
              new Blob([json], { type: 'application/fhir+json' }),
              `${resource.resourceType}-${resource.id ?? 'resource'}.json`,
            )
          }
        >
          Download
        </Button>
        <Button
          type="button"
          variant="primary"
          telemetryInteractionName="resource_action_click"
          telemetryContext={{ resourceActionName: 'Copy FHIR JSON' }}
          onClick={() =>
            copyToClipboard(json, () => notify({ type: 'info', title: 'Copied FHIR JSON' }))
          }
        >
          Copy JSON
        </Button>
      </div>
    </div>
  );
};

export function useViewFhirModal() {
  const telemetry = useTelemetry();
  const { openModal } = useModal({
    boundaryName: 'ViewFHIRModal',
    layout: { overflowBehavior: 'hidden', mode: 'full', fullbleed: false, showCloseButton: true },
  });

  return (resource: Resource, title = 'View FHIR') => {
    openModal({
      telemetry,
      action: { content: { body: <ViewFhirModalBody title={title} resource={resource} /> } },
    });
  };
}
